import { Card, Inline, Stack, breakpoints } from '@etg/wings';
import { useDateFormat, useTranslation } from '@eti/providers';
import { css, mediaQueries } from '@eti/styles';
import { date } from '@eti/utils';
import { getStopoverType } from '../../../../../../../utils/format';
import {
  getBoundTitle,
  getBoundType,
} from '../../../../../../../widgets/trip-details/utils/format';
import { TripDetailsDuration as DurationAndStops } from '../TripDetailsDuration';
import { TripDetailsLocationTimeAndDate } from '../TripDetailsLocationTimeAndDate';
import { TripDetailsBoundCarrierLogo } from '../TripDetailsBoundCarrierLogo';
import { SelfTransferIndicator, StopoverInfoIndicator } from '../TripIndicator';
import { useBrandedFareName } from '../../../../../../../common/custom-hooks/useBrandedFareName';
import type { TripDetailsBoundProps } from './model';
import useGenerateTripBoundAriaLabel from './hooks/useGenerateTripBoundAriaLabel';

const tripTypeTextStyles = css`
  font-size: 1rem;
  font-weight: 700;
`;

const sectionStyles = css`
  border-bottom: 0;
  overflow: hidden;
  padding: 8px 16px;

  :first-child,
  :last-child {
    margin: 0;
    padding: 8px 16px;
  }
`;

const dotSeparatorStyles = css`
  background-color: #737373;
  border-radius: 2px;
  height: 3px;
  width: 3px;
`;

const cabinClassStyles = css`
  color: #737373;
`;

const dateAndTimeClass = css`
  @media ${mediaQueries.medium.up} {
    font-size: 1.25rem;
  }

  @media ${mediaQueries.large.up} {
    font-size: 1.375rem;
  }
`;

export const selfTransferIndicatorWrapper = (numberOfMarketingCarriers: number) => css`
  width: calc(100% - 46px);

  @media ${mediaQueries.medium.up} {
    margin-top: -4px;
  }

  @media (min-width: ${breakpoints._1440}) {
    width: ${numberOfMarketingCarriers > 1 ? 'calc(100% - 84px)' : 'calc(100% - 76px)'};
  }
`;

export const TripDetailsBoundA = ({
  boundAsSegment,
  boundNumber,
  flightId,
  includesTrainSegments,
  isMultiStop,
  marketingCarriers,
  segments,
  isDifferentDestinationAirport,
  isDifferentOriginAirport,
}: TripDetailsBoundProps) => {
  const { t } = useTranslation();
  const { day } = useDateFormat();
  const {
    arrivedAt,
    brandedFareInformation,
    cabinClassName,
    departuredAt,
    destination,
    duration,
    marketingCarrier,
    origin,
    stops,
    isSelfTransfer,
  } = boundAsSegment;
  const stopoverType = getStopoverType(segments);
  const title = t(getBoundTitle(getBoundType(boundNumber - 1), isMultiStop), boundNumber);
  const tripBoundAriaLabel = useGenerateTripBoundAriaLabel(
    boundAsSegment,
    marketingCarriers,
    title,
    includesTrainSegments,
  );
  const brandedFareInfoAndMarketingCarrier = { brandedFareInformation, marketingCarrier };
  const brandedFareName = useBrandedFareName(brandedFareInfoAndMarketingCarrier);
  const numberOfTechnicalStops =
    segments
      ?.filter((segment) => segment.__typename === 'TripSegment')
      .reduce((acc, segment) => {
        if (Number.isFinite(segment.numberOfTechnicalStops)) {
          return acc + segment.numberOfTechnicalStops;
        }

        return acc;
      }, 0) ?? 0;
  const durationInDays = date.durationInDays(new Date(departuredAt), new Date(arrivedAt));

  return (
    <Card.Section
      aria-label={tripBoundAriaLabel}
      className={sectionStyles}
      data-testid={`resultPage-resultTrip-${flightId}-bound-${boundNumber}-wrapper`}
      padding={4}
    >
      <Stack aria-hidden spacing={8}>
        <Inline alignY="center" spacing={8}>
          <div data-testid={`trip-bound-${boundNumber}-tripType`}>
            <div className={tripTypeTextStyles}>{title}</div>
          </div>
          <div data-testid={`trip-bound-${boundNumber}-info-date`}>
            {date.format(departuredAt, day)}
          </div>
          <div className={dotSeparatorStyles} />
          <div
            className={cabinClassStyles}
            data-testid={`trip-bound-${boundNumber}-cabinClass-info`}
          >
            {brandedFareName || t(`IbeClient.CabinClass.${cabinClassName}.Text`)}
          </div>
        </Inline>
        <Inline align="center" alignY="top" noWrap spacing={8}>
          <TripDetailsLocationTimeAndDate
            airportCode={origin.airportCode ?? origin.code}
            boundNumber={boundNumber}
            city={origin.cityName}
            className={dateAndTimeClass}
            dateAndTime={departuredAt}
            isDifferentAirport={isDifferentOriginAirport}
            type="departure"
          />
          <DurationAndStops
            boundNumber={boundNumber}
            duration={duration}
            includesTrainSegments={includesTrainSegments}
            numberOfTechnicalStops={numberOfTechnicalStops}
            stops={stops}
          />
          <TripDetailsLocationTimeAndDate
            airportCode={destination.airportCode ?? destination.code}
            boundNumber={boundNumber}
            city={destination.cityName}
            className={dateAndTimeClass}
            dateAndTime={arrivedAt}
            durationInDays={durationInDays}
            isDifferentAirport={isDifferentDestinationAirport}
            type="arrival"
          />
          <TripDetailsBoundCarrierLogo marketingCarriers={marketingCarriers} />
        </Inline>
        <Inline
          align="center"
          className={selfTransferIndicatorWrapper(marketingCarriers.length)}
          spacing={4}
        >
          {isSelfTransfer && <SelfTransferIndicator boundNumber={boundNumber} />}
          {stopoverType && <StopoverInfoIndicator stopoverType={stopoverType} />}
        </Inline>
      </Stack>
    </Card.Section>
  );
};
